/**
 * ConduitLayer — 3D cylinders representing conduits in the electrical
 * room scene. Extracted from `scene.js` (iter 40) to bring that file
 * under the 700-line refactor threshold.
 *
 * Conduits ride along:
 *   · every `drop` tray (legacy behaviour — drops always get a conduit)
 *   · `run` trays that are explicitly tagged with `conduit_type`, OR
 *     when `project.runs_as_conduits` is true (project-wide opt-in for
 *     "treat horizontal runs as conduit raceways too" mode).
 *
 * CRITICAL: This module uses `React.createElement` (the `h` alias) NOT
 * JSX. The project-wide visual-edits babel plugin injects line-number
 * props on JSX elements that Three.js then refuses to consume.
 */
import React from "react";
import { Html } from "@react-three/drei";
import { IN_TO_M } from "./scene";

const h = React.createElement;

/* Material palette per code-acceptable conduit type. Picks `project.conduit_type`
   (set via /conduit-schedule call) as the default; falls back to EMT.            */
const CONDUIT_COLORS = {
  EMT:  { color: "#C0C5CE", metalness: 0.9,  roughness: 0.25 },   // galv steel
  RMC:  { color: "#6b6f76", metalness: 0.85, roughness: 0.35 },   // dark galv
  IMC:  { color: "#8d9199", metalness: 0.85, roughness: 0.35 },   // medium
  PVC:  { color: "#D9D6CE", metalness: 0.05, roughness: 0.7  },   // off-white
  FMC:  { color: "#7a7e85", metalness: 0.6,  roughness: 0.55 },   // flex grey
  ENT:  { color: "#3F76C2", metalness: 0.05, roughness: 0.7  },   // blue PVC ENT
  LFMC: { color: "#2c2c2c", metalness: 0.1,  roughness: 0.6  },   // black jacket
};

export default function ConduitLayer({ project }) {
  const runsAsConduits = !!project.runs_as_conduits;
  const segs = (project.cable_trays || []).filter((s) => {
    if (s.kind === "drop") return true;
    if (s.kind === "run")  return !!s.conduit_type || runsAsConduits;
    return false;
  });
  if (segs.length === 0) return null;
  const room_H_in = project.depth_in || 96;
  const ceilingMountIn = room_H_in - 12;
  const bottomIn = 36;
  const radius = 0.75 * IN_TO_M;

  const meshes = segs.map((s, idx) => {
    if (!s.points || s.points.length < 1) return null;
    const ct = s.conduit_type || project.conduit_type || "EMT";
    const mat = CONDUIT_COLORS[ct] || CONDUIT_COLORS.EMT;
    if (s.kind === "run") {
      // Horizontal run conduit — a cylinder lying along the run axis at
      // the run's elevation_in (or default ceiling height when unset).
      const p0 = s.points[0];
      const p1 = s.points[1] || p0;
      const dx = (p1[0] - p0[0]) * IN_TO_M;
      const dz = (p1[1] - p0[1]) * IN_TO_M;
      const len = Math.hypot(dx, dz);
      if (len < 0.01) return null;
      const cx = ((p0[0] + p1[0]) / 2) * IN_TO_M;
      const cz = ((p0[1] + p1[1]) / 2) * IN_TO_M;
      const cy = (s.elevation_in || ceilingMountIn) * IN_TO_M;
      // Default cylinder axis is Y; rotate Z by 90° → horizontal, then
      // yaw around Y so the cylinder lies along the run vector in plan.
      const yaw = Math.atan2(dz, dx);
      return h("group", { key: `cond-${s.id || idx}`,
                          position: [cx, cy, cz],
                          rotation: [0, -yaw, Math.PI / 2] },
        h("mesh", {},
          h("cylinderGeometry", { args: [radius, radius, len, 16] }),
          h("meshStandardMaterial", { color: mat.color, metalness: mat.metalness, roughness: mat.roughness }),
        ),
        h(Html, {
          position: [0, 0, 0], center: true, distanceFactor: 6, pointerEvents: "none",
        }, h("div", {
          style: {
            background: mat.color, color: "#0A0C10", fontFamily: "ui-monospace,monospace",
            fontSize: 9, padding: "1px 4px", fontWeight: 700, letterSpacing: 0.5,
            border: "1px solid rgba(0,0,0,0.35)", whiteSpace: "nowrap",
          },
        }, ct)),
      );
    }
    // Vertical drop conduit — legacy path preserved.
    const [x1, y1] = s.points[0];
    const len_in = ceilingMountIn - bottomIn;
    const cx = (x1 + 4) * IN_TO_M;
    const cz = y1 * IN_TO_M;
    const cy = ((ceilingMountIn + bottomIn) / 2) * IN_TO_M;
    return h("group", { key: `cond-${s.id || idx}` },
      h("mesh", { position: [cx, cy, cz] },
        h("cylinderGeometry", { args: [radius, radius, len_in * IN_TO_M, 16] }),
        h("meshStandardMaterial", { color: mat.color, metalness: mat.metalness, roughness: mat.roughness }),
      ),
      h(Html, {
        position: [cx, (ceilingMountIn - 6) * IN_TO_M, cz],
        center: true, distanceFactor: 6, pointerEvents: "none",
      }, h("div", {
        style: {
          background: mat.color, color: "#0A0C10", fontFamily: "ui-monospace,monospace",
          fontSize: 9, padding: "1px 4px", fontWeight: 700, letterSpacing: 0.5,
          border: "1px solid rgba(0,0,0,0.35)", whiteSpace: "nowrap",
        },
      }, ct)),
    );
  }).filter(Boolean);

  return h("group", null, ...meshes);
}
